export const DASH = '–';

export type Tone = 'up' | 'down' | 'flat';

/** One line of a card's expandable details: a measure's value at the start and end of the range. */
export type DetailRow = { label: string; start: number; end: number };

const isNumber = (value: unknown): value is number => typeof value === 'number' && isFinite(value);

export function formatNumber(value: unknown, decimals = 0): string {
  if (!isNumber(value)) return DASH;
  return value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** Whole numbers, and `125k` from 100,000 up, so a total still fits the narrow column of a card. */
export function formatCompact(value: unknown): string {
  if (!isNumber(value)) return DASH;
  return Math.abs(value) >= 100000 ? `${formatNumber(value / 1000, 0)}k` : formatNumber(value, 0);
}

/** `+12.5` / `−3.0` / `0.0`, with a real minus sign so it lines up with the plus. */
export function formatChange(value: unknown, decimals = 0): string {
  if (!isNumber(value)) return DASH;
  const text = formatNumber(Math.abs(value), decimals);
  // A change that rounds to zero shows as zero, not "+0.0".
  if (Number(text.replace(/,/g, '')) === 0) return text;
  return `${value > 0 ? '+' : '−'}${text}`;
}

export function changeTone(value: unknown, decimals = 0): Tone {
  if (!isNumber(value)) return 'flat';
  const rounded = Math.round(value * 10 ** decimals) / 10 ** decimals;
  return rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat';
}

/** Seconds as `h:mm`; hours aren't capped at 24 (a year of riding is a few hundred). */
export function formatHours(seconds: unknown): string {
  if (!isNumber(seconds)) return DASH;
  const totalMinutes = Math.round(seconds / 60);
  return `${Math.floor(totalMinutes / 60)}:${String(totalMinutes % 60).padStart(2, '0')}`;
}

/** Seconds as `m:ss` (a focus duration is minutes, so no hours part). */
export function formatMMSS(seconds: unknown): string {
  if (!isNumber(seconds) || seconds < 0) return DASH;
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** A total spread over the range as a weekly rate. NaN (shown as a dash) when the range has no length. */
export function perWeek(total: unknown, rangeDays: unknown): number {
  if (!isNumber(total) || !isNumber(rangeDays) || rangeDays <= 0) return NaN;
  return total / (rangeDays / 7);
}
